import type { RefObject } from 'react';
import React from 'react';
import cx from 'classnames';
import type { QueryStatus } from '@tanstack/query-core';

import { ArrowLeftIcon, ArrowRightIcon } from '@motorway/mw-highway-code';

import { QueryCoreStatus } from 'Types/queryCoreStatus';
import { applyCypressData } from 'Utilities/helpers';

import { PaginationButton } from './components/PaginationButton';
import { PaginationText } from './components/PaginationText';
import { getButtonState, getPaginationState } from './Pagination.helpers';
import type { PaginationDirections } from './Pagination.types';

import styles from './Pagination.module.scss';

export type PaginationProps = {
	containerClassName?: string;
	cypressId?: string;
	isDisabled?: boolean;
	onClick?: (page: number, direction: PaginationDirections) => void;
	page: number;
	queryStatus: QueryStatus;
	recordsPerPage: number;
	scrollToOffset?: number;
	scrollToRef?: RefObject<HTMLElement>;
	textBetweenButtons?: boolean;
	textBetweenButtonsClassName?: string;
	totalRecords?: number;
};

const Pagination: React.FC<PaginationProps> = ({
	containerClassName,
	cypressId = 'pagination',
	isDisabled = false,
	onClick,
	page,
	queryStatus,
	recordsPerPage,
	scrollToOffset = 0,
	scrollToRef,
	textBetweenButtons = false,
	textBetweenButtonsClassName,
	totalRecords = 0,
}) => {
	const paginationMeta = {
		page,
		recordsPerPage,
		totalRecords,
	};

	const isPaginationDisabled =
		getPaginationState(isDisabled, totalRecords) ||
		queryStatus === QueryCoreStatus.Loading ||
		queryStatus === QueryCoreStatus.Error;
	const isBackButtonDisabled = getButtonState('back', paginationMeta);
	const isForwardButtonDisabled = getButtonState('forward', paginationMeta);

	const onClickHandler = (isButtonDisabled: boolean, pageNumber: number, direction: PaginationDirections): void => {
		if (!isButtonDisabled) {
			onClick?.(pageNumber, direction);

			setTimeout(() => {
				if (scrollToRef?.current) {
					const y = scrollToRef.current.getBoundingClientRect().top + window.scrollY + scrollToOffset;
					window.scrollTo({ behavior: 'smooth', top: y });
				}
			}, 0);
		}
	};

	const text = (
		<PaginationText
			isEmptyState={isPaginationDisabled}
			isLoading={queryStatus === QueryCoreStatus.Loading}
			paginationMeta={paginationMeta}
		/>
	);

	return (
		<nav
			{...applyCypressData(cypressId)}
			aria-label="Pagination navigation"
			className={cx(styles.container, containerClassName)}
			data-testid="pagination"
			role="navigation"
		>
			{!textBetweenButtons && text}
			<ul className={styles.controls}>
				<li className={styles.controlItem}>
					<PaginationButton
						cypressId={cypressId}
						direction="back"
						icon={ArrowLeftIcon}
						isDisabled={isPaginationDisabled || isBackButtonDisabled}
						onClick={onClickHandler}
						page={page}
					/>
				</li>
				{textBetweenButtons && <li className={cx(styles.textBetweenButtons, textBetweenButtonsClassName)}>{text}</li>}
				<li className={styles.controlItem}>
					<PaginationButton
						cypressId={cypressId}
						direction="forward"
						icon={ArrowRightIcon}
						isDisabled={isPaginationDisabled || isForwardButtonDisabled}
						onClick={onClickHandler}
						page={page}
					/>
				</li>
			</ul>
		</nav>
	);
};

export default Pagination;
